/**
 * Empty State component.
 * Premium empty state with gradient icon and modern CTA button. CoOrdio DS icons (Lucide).
 */

import type { ReactNode } from 'react';
import { Button } from '@coordio/ui';
import { Plus as AddIcon } from 'lucide-react';
import { Inbox } from '@core/components/icons';

/**
 * Empty State component props
 */
export interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  height?: number | string;
}

/**
 * Empty State component with modern glassmorphism design.
 */
const EmptyState = ({
  title = 'No data available',
  description = 'There is no data to display at this time.',
  icon,
  actionLabel,
  onAction,
  height = 400,
}: EmptyStateProps) => {
  const iconContent = icon ?? <Inbox size={32} />;
  return (
    <div
      className="flex flex-col items-center justify-center p-6 text-center rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg-subtle)]"
      style={{ minHeight: height }}
    >
      {/* Gradient Icon Container */}
      <div className="w-[72px] h-[72px] rounded-xl bg-[var(--gradient-primary-subtle)] flex items-center justify-center mb-4 animate-[float_3s_ease-in-out_infinite]">
        <div className="text-[2rem] text-accent leading-none flex items-center justify-center">
          {iconContent}
        </div>
      </div>

      <h6 className="text-lg font-semibold mb-1.5">{title}</h6>
      <p
        className={`text-sm text-muted-foreground max-w-[420px] leading-relaxed ${actionLabel ? 'mb-5' : 'mb-0'}`}
      >
        {description}
      </p>
      {actionLabel && onAction && (
        <Button onClick={onAction}>
          <AddIcon className="mr-2 h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default EmptyState;
